import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi
} from '@angular/common/http';
import {
  ErrorHandler, inject, NgModule, provideAppInitializer
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { DialogModule } from '@ngneat/dialog';
import {
  popperVariation, provideTippyConfig, tooltipVariation, withContextMenuVariation
} from '@ngneat/helipopper';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { HighchartsChartModule } from 'highcharts-angular';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { DEFAULT_DIALOG_OPTIONS } from '~core/constants/default-dialog-options.constant';
import { statusInstancesVariation } from '~core/constants/tippy-status-instances-variation';
import { ApiUrlInterceptorService } from '~core/interceptors/api-url-interceptor.service';
import { NullPatchInterceptor } from '~core/interceptors/null-patch.interceptor';
import { AppService, InitializeAppState } from '~core/services/app/app.service';
import { initializeLoading, LoadingService } from '~core/services/loading/loading.service';
import { initializePermitEditState, PermitEditState } from '~permit/permit-edit/state/permit-edit.state';
import { TranslatePipe } from '~shared/pipes/translate.pipe';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { CoreModule } from './core/core.module';
import { DateModule } from './date/date.module';
import { OfflineModule } from './offline/offline.module';
import { SignalrModule } from './signalr/signalr.module';

@NgModule({
  declarations: [ AppComponent ],
  bootstrap: [ AppComponent ],
  imports: [
    AppRoutingModule,
    AuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    CommonModule,
    DialogModule.forRoot(),
    DateModule,
    FormsModule,
    MatBottomSheetModule,
    MatButtonModule,
    MatDialogModule,
    MatPaginatorModule,
    MatSnackBarModule,
    MatIconModule,
    MatInputModule,
    QuillModule.forRoot({
      theme: 'snow',
      modules: {
        imageResize: {
          modules: [ 'Resize', 'DisplaySize' ]
        }
      }
    }),
    PdfViewerModule,
    ReactiveFormsModule,
    RouterModule,
    ToastrModule.forRoot(),
    SignalrModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    OfflineModule,
    HighchartsChartModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'), // Optional, add line numbers if needed
      languages: {
        json: () => import('highlight.js/lib/languages/json')
      }
    }),
    provideHotToastConfig({
      autoClose: true,
      duration: 6000,
      position: 'top-right'
    }),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: {
          ...tooltipVariation,
          theme: 'light',
          arrow: true
        },
        popper: popperVariation,
        contextMenu: withContextMenuVariation(popperVariation),
        statusInstances: statusInstancesVariation
      }
    }),
    TranslatePipe,
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        closeOnNavigation: true
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiUrlInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NullPatchInterceptor,
      multi: true
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: DEFAULT_DIALOG_OPTIONS
    },
    Title,
    provideAppInitializer(() => {
      const initializerFn = (InitializeAppState)(inject(AppService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (initializeLoading)(inject(LoadingService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (initializePermitEditState)(inject(PermitEditState));
      return initializerFn();
    }),
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService
    }
  ]
})
export class AppModule { }

